import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  BankOutlined,
  CheckOutlined,
  PrinterOutlined,
  SaveOutlined,
  SendOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { PrintableHrmsTransaction } from '@/components/hr/PrintableHrmsTransaction';
import { ENTITY_TYPE_HR_EOSB_SETTLEMENT } from '@/constants/approval-entity-types';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { buildLinearWorkflow, hrmsStatusColor, showApprovalPanel } from './shared/hrmsStatus';
import { getOrFindFromList } from './shared/hrmsFetch';
import { openHrmsPrintOverlay } from './shared/openHrmsPrintOverlay';
import employeeService from '@/services/employee.service';
import { mocService } from '@/services/moc.service';
import { describeResponsibilities, handoverMocUrl } from '@/components/moc/handoverPrompt';

const { Text } = Typography;

export const EOSB_CHECKLIST_ITEMS: { key: string; label: string }[] = [
  { key: 'assets_returned', label: 'Company assets returned' },
  { key: 'access_revoked', label: 'System / badge access revoked' },
  { key: 'loans_cleared', label: 'Outstanding loans cleared or deducted' },
  { key: 'leave_settled', label: 'Leave balance settled / encashed' },
  { key: 'documents_collected', label: 'Exit documents collected' },
  { key: 'final_calc_reviewed', label: 'Final settlement calculation reviewed' },
];

function normalizeChecklist(raw: unknown): Record<string, boolean> {
  const base: Record<string, boolean> = {};
  for (const item of EOSB_CHECKLIST_ITEMS) base[item.key] = false;
  if (!raw) return base;
  if (Array.isArray(raw)) {
    for (const row of raw) {
      if (row && typeof row === 'object' && 'key' in row) {
        const r = row as { key: string; done?: boolean };
        if (r.key in base) base[r.key] = Boolean(r.done);
      }
    }
    return base;
  }
  if (typeof raw === 'object') {
    const src = raw as Record<string, unknown>;
    for (const item of EOSB_CHECKLIST_ITEMS) {
      base[item.key] = Boolean(src[item.key]);
    }
  }
  return base;
}

const EosbDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { empLabel } = useEmployeeOptions({ employment_status: null });
  const [loading, setLoading] = useState<string | null>(null);
  const [checklist, setChecklist] = useState<Record<string, boolean>>(() => normalizeChecklist(null));

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.eosbSettlement(id || ''),
    queryFn: () =>
      getOrFindFromList(
        () => hrmsService.getEosbSettlement(id!),
        () => hrmsService.listEosbSettlements(),
        id!
      ),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!row) return;
    setChecklist(normalizeChecklist(row.checklist || row.checklist_json || row.exit_checklist));
  }, [row]);

  const { data: handoverCheck } = useQuery({
    queryKey: ['eosb-handover-check', row?.employee_id],
    enabled: Boolean(row?.employee_id),
    retry: false,
    queryFn: async () => {
      const emp = await employeeService.getById(row!.employee_id);
      if (!emp?.user_id) return null;
      const summary = await mocService.getResponsibilities(emp.user_id);
      return { userId: emp.user_id, summary };
    },
  });

  const incompleteItems = useMemo(
    () => EOSB_CHECKLIST_ITEMS.filter((i) => !checklist[i.key]),
    [checklist]
  );
  const checklistComplete = incompleteItems.length === 0;

  const workflow = useMemo(
    () => buildLinearWorkflow(['Draft', 'Pending', 'Approved', 'Paid'], row?.status || 'Draft'),
    [row?.status]
  );

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.eosbSettlement(id!) });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.eosbSettlements() });
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="EOSB settlement" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="EOSB settlement" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const saveChecklist = async () => {
    setLoading('checklist');
    try {
      await hrmsService.saveEosbChecklist(id!, checklist);
      message.success('Checklist saved');
      await invalidate();
    } catch {
      message.error('Checklist save failed');
    } finally {
      setLoading(null);
    }
  };

  const submit = async () => {
    if (!checklistComplete) {
      message.warning(
        `Complete checklist before submit: ${incompleteItems.map((i) => i.label).join(', ')}`
      );
      return;
    }
    setLoading('submit');
    try {
      await hrmsService.submitEosb(id!);
      message.success('Submitted');
      await invalidate();
    } catch {
      message.error('Submit failed');
    } finally {
      setLoading(null);
    }
  };

  const markPaid = async () => {
    setLoading('paid');
    try {
      await hrmsService.markEosbPaid(id!);
      message.success('Settlement marked paid');
      await invalidate();
    } catch {
      message.error('Mark paid failed');
    } finally {
      setLoading(null);
    }
  };

  const glHandoff = async () => {
    setLoading('gl');
    try {
      const result = await hrmsService.createEosbSettlementGlHandoff(id!);
      message.success(
        result?.journal_voucher_id
          ? `Draft JV created: ${result.journal_voucher_id}`
          : 'Settlement GL handoff completed (draft JV)'
      );
      await invalidate();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'GL handoff failed');
    } finally {
      setLoading(null);
    }
  };

  const canEditChecklist = row.status === 'Draft' || row.status === 'Pending';
  const calcSteps = (row.calc_json as any)?.steps || [];

  const printDoc = () => {
    const doneCount = EOSB_CHECKLIST_ITEMS.filter((i) => checklist[i.key]).length;
    openHrmsPrintOverlay((onClose) => (
      <PrintableHrmsTransaction
        onClose={onClose}
        data={{
          docType: 'HR_EOSB_SETTLEMENT',
          title: 'EOSB Settlement',
          documentNumber: row.settlement_number,
          employee_name: empLabel(row.employee_id),
          status: row.status,
          meta: [
            { label: 'Exit type', value: String(row.exit_type || '—') },
            {
              label: 'Last working',
              value: row.last_working_date
                ? dayjs(row.last_working_date).format('DD-MMM-YYYY')
                : '—',
            },
            { label: 'Service years', value: String(row.service_years ?? '—') },
          ],
          lines: [
            { description: 'Gratuity', value: String(row.gratuity_amount ?? '—') },
            { description: 'Leave encashment', value: String(row.leave_encashment ?? '—') },
            { description: 'Other dues', value: String(row.other_dues ?? '—') },
            { description: 'Recoveries', value: String(row.recoveries ?? '—') },
            { description: 'Net payable', value: String(row.net_payable ?? '—') },
            {
              description: 'Checklist',
              value: `${doneCount}/${EOSB_CHECKLIST_ITEMS.length} complete`,
            },
            ...EOSB_CHECKLIST_ITEMS.map((item) => ({
              description: item.label,
              value: checklist[item.key] ? 'Done' : 'Pending',
            })),
          ],
          totals: [{ label: 'Net payable', value: String(row.net_payable ?? '—') }],
          notes: row.notes,
        }}
      />
    ));
  };

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="EOSB settlement"
          documentNumber={row.settlement_number  || '—'}
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{row.status}</Tag>}
          subtitle={<Text type="secondary">{empLabel(row.employee_id)}</Text>}
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/eosb')}>
                Back
              </Button>
              {row.employee_id && (
                <Button
                  icon={<UserOutlined />}
                  onClick={() => navigate(`/hr/eosb/profile/${row.employee_id}`)}
                >
                  EOSB profile
                </Button>
              )}
              <Button icon={<PrinterOutlined />} onClick={printDoc}>
                Print statement
              </Button>
              {(row.status === 'Draft' || row.status === 'Pending') && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={loading === 'submit'}
                  onClick={submit}
                >
                  Submit
                </Button>
              )}
              {(row.status === 'Approved' || row.status === 'Paid') && (
                <Button icon={<BankOutlined />} loading={loading === 'gl'} onClick={glHandoff}>
                  Create draft JV (liability utilization)
                </Button>
              )}
              {row.status === 'Approved' && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={loading === 'paid'}
                  onClick={markPaid}
                >
                  Mark paid
                </Button>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Settlement" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Employee">{empLabel(row.employee_id)}</Descriptions.Item>
              <Descriptions.Item label="Exit type">{row.exit_type}</Descriptions.Item>
              <Descriptions.Item label="Last working">
                {row.last_working_date ? dayjs(row.last_working_date).format('DD-MMM-YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Service years">{row.service_years ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Gratuity">{row.gratuity_amount ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Leave encashment">{row.leave_encashment ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Other dues">{row.other_dues ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Recoveries">{row.recoveries ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Auto loan recoveries">
                {(row.calc_json as any)?.recoveries_auto != null
                  ? String((row.calc_json as any).recoveries_auto)
                  : '—'}
                {(row.calc_json as any)?.open_loans
                  ? ` (${(row.calc_json as any).open_loans} open)`
                  : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Open assets">
                {(row.calc_json as any)?.open_assets != null
                  ? String((row.calc_json as any).open_assets)
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Net payable">{row.net_payable ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Paid at">
                {row.paid_at ? dayjs(row.paid_at).format('DD-MMM-YYYY HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Notes">{row.notes || '—'}</Descriptions.Item>
            </Descriptions>
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="Gratuity is the EOSB statutory component. Leave encashment and other dues are separate final-settlement lines. GL handoff utilizes EOSB liability against gratuity only."
            />
            {(row.calc_json as any)?.governance?.salary_override && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message="Basic salary override applied (Employee Master unchanged)"
                description={`Reason: ${(row.calc_json as any).governance.salary_override.reason}; effective ${
                  (row.calc_json as any).governance.salary_override.effective_date
                }; material=${Boolean(
                  (row.calc_json as any).governance.salary_override.material
                )}`}
              />
            )}
            {(row.calc_json as any)?.governance?.gratuity_exception?.exception_requested && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message="Request Gratuity Exception on file"
                description={
                  (row.calc_json as any).governance.gratuity_exception.justification ||
                  (row.calc_json as any).governance.gratuity_exception.escalation_note
                }
              />
            )}
          </Card>

          {calcSteps.length > 0 && (
            <Card title="Calculation steps" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {calcSteps.map((s: any) => (
                  <div key={s.step}>
                    <Text strong>
                      {s.step}. {s.title}
                    </Text>
                    <div>
                      <Text type="secondary">{s.detail}</Text>
                      {s.amount != null && (
                        <Text style={{ marginLeft: 8 }}>{String(s.amount)}</Text>
                      )}
                    </div>
                  </div>
                ))}
              </Space>
            </Card>
          )}

          <Card
            title="Exit checklist"
            style={{ marginBottom: 16 }}
            extra={
              canEditChecklist && (
                <Button
                  size="small"
                  icon={<SaveOutlined />}
                  loading={loading === 'checklist'}
                  onClick={saveChecklist}
                >
                  Save checklist
                </Button>
              )
            }
          >
            {!checklistComplete && (row.status === 'Draft' || row.status === 'Pending') && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Checklist incomplete — submit is blocked until all items are checked"
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {incompleteItems.map((i) => (
                      <li key={i.key}>{i.label}</li>
                    ))}
                  </ul>
                }
              />
            )}
            {handoverCheck?.summary?.has_any && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="System access: approval responsibilities not handed over yet"
                description={
                  <>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {describeResponsibilities(handoverCheck.summary).map((l) => (
                        <li key={l}>{l}</li>
                      ))}
                    </ul>
                    <Button
                      size="small"
                      style={{ marginTop: 8 }}
                      onClick={() =>
                        navigate(
                          handoverMocUrl(
                            handoverCheck.userId,
                            /terminat/i.test(String(row.exit_type || '')) ? 'termination' : 'resignation'
                          )
                        )
                      }
                    >
                      Raise handover MOC
                    </Button>
                  </>
                }
              />
            )}
            <Space direction="vertical" style={{ width: '100%' }}>
              {EOSB_CHECKLIST_ITEMS.map((item) => (
                <Checkbox
                  key={item.key}
                  checked={Boolean(checklist[item.key])}
                  disabled={!canEditChecklist}
                  onChange={(e) =>
                    setChecklist((prev) => ({ ...prev, [item.key]: e.target.checked }))
                  }
                >
                  {item.label}
                </Checkbox>
              ))}
            </Space>
          </Card>

          <EntityAttachmentsCard entityType="HR_EOSB_SETTLEMENT" entityId={row.id} />
        </Col>
        <Col xs={24} lg={8}>
          {showApprovalPanel(row) && (
            <ApprovalPanel
              entityType={ENTITY_TYPE_HR_EOSB_SETTLEMENT}
              entityId={row.id}
              extraInvalidateQueryKeys={[hrmsKeys.eosbSettlement(id!), hrmsKeys.eosbSettlements()]}
            />
          )}
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default EosbDetailPage;
